import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Lightning, Sparkle, ChartLineUp, CurrencyDollar, Users, ArrowRight, CheckCircle, GoogleLogo, Ticket, EnvelopeSimple, Calendar, MapPin, Info, Quotes, Star, Microphone } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";
import EmailAuthModal from "../components/EmailAuthModal";
import FoundingSpotsCounter from "../components/FoundingSpotsCounter";
import ClosingSoonTicker from "../components/ClosingSoonTicker";
import DynamicGrantCount, { DynamicScoutCount, usePublicStats } from "../components/DynamicGrantCount";

const LandingPage = () => {
  const { user, login, refreshUser } = useAuth();
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [showEmailAuth, setShowEmailAuth] = useState(false);
  const [pricingAudience, setPricingAudience] = useState("nonprofit"); // nonprofit | business | franchisee
  const isBizOrFranchisee = pricingAudience === "business" || pricingAudience === "franchisee";

  const redeemPromo = async () => {
    if (!promoCode.trim()) { toast.error("Please enter a promo code"); return; }
    if (!user) {
      // Save promo code before redirecting to login
      localStorage.setItem("pending_promo_code", promoCode.trim().toUpperCase());
      login();
      return;
    }
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/redeem`, { code: promoCode });
      toast.success(res.data.message);
      setPromoCode("");
      localStorage.removeItem("pending_promo_code");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem promo code");
    } finally { setPromoLoading(false); }
  };

  const features = [
    {
      icon: <Lightning weight="duotone" className="w-8 h-8 text-emerald-400" />,
      title: "Flash Draft",
      description: "Grant applications generated in seconds. Our system crafts compelling proposals tailored to each grant and your nonprofit profile."
    },
    {
      icon: <Sparkle weight="duotone" className="w-8 h-8 text-emerald-400" />,
      title: "One-Tap Profile",
      description: "Set up your nonprofit profile once. Auto-fill applications with your organization's information instantly."
    },
    {
      icon: <ChartLineUp weight="duotone" className="w-8 h-8 text-emerald-400" />,
      title: "Post-Grant Manager",
      description: "Track budgets, milestones, and generate Flash Reports. Stay compliant and organized after winning."
    },
    {
      icon: <CurrencyDollar weight="duotone" className="w-8 h-8 text-emerald-400" />,
      title: "Micro-Grants",
      description: "Access $500-$5,000 grants specifically for South Carolina nonprofits. Quick funding for immediate impact."
    }
  ];

  // Live grant + scout counts so marketing copy stays current after each Friday research session.
  const publicStats = usePublicStats();
  const totalGrants = publicStats?.total_active ?? 100;
  const totalScouts = publicStats?.corporate_scouts ?? 30;

  const stats = [
    { value: "$11M+", label: "In Tracked Funding" },
    { value: String(totalGrants), label: "Verified SC Grants" },
    { value: String(totalScouts), label: "Corporate Funders" },
    { value: "46", label: "SC Counties Covered" }
  ];

  // Public preview of currently active grants — no auth required
  const [previewGrants, setPreviewGrants] = useState([]);
  useEffect(() => {
    axios.get(`${API}/grants?sort_by=deadline&sort_order=asc&limit=5`)
      .then(res => {
        const list = res.data?.grants || res.data?.items || [];
        setPreviewGrants(list.slice(0, 5));
      })
      .catch(() => setPreviewGrants([]));
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning weight="fill" className="w-8 h-8 text-emerald-500" />
            <span className="text-xl font-bold tracking-tight">FlashGrants</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-zinc-400 hover:text-white transition-colors text-sm">Features</a>
            <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors text-sm">Pricing</a>
            <a href="/start-a-nonprofit-in-sc" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="nav-start-nonprofit">Free Roadmap</a>
            <a href="#about" className="text-zinc-400 hover:text-white transition-colors text-sm">About</a>
          </nav>

          {user ? (
            <Button 
              onClick={() => window.location.href = '/dashboard'}
              className="bg-emerald-500 text-black font-bold rounded-sm hover:bg-emerald-400 cta-hover"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setShowEmailAuth(true)}
                variant="outline"
                className="border-zinc-700 text-white hover:border-zinc-500 rounded-sm flex items-center gap-2"
                data-testid="email-signin-btn"
              >
                <EnvelopeSimple weight="bold" className="w-4 h-4" />
                Sign In
              </Button>
              <Button 
                onClick={login}
                className="bg-emerald-500 text-black font-bold rounded-sm hover:bg-emerald-400 cta-hover flex items-center gap-2"
                data-testid="login-btn"
              >
                <GoogleLogo weight="bold" className="w-4 h-4" />
                Google
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section — Editorial / Asymmetric ✦ Shipped 2026-06-03 */}
      <section
        className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Myrtle Beach pier photograph — David Frye / Unsplash · Myrtle Beach, SC */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1697688494916-6152ba0d4919?fm=jpg&q=85&w=2400&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center 35%",
            opacity: 0.32,
            filter: "contrast(1.1) saturate(1.05)",
          }}
          aria-hidden="true"
        />
        {/* Two-tone veil keeps copy legible against the photograph */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,5,5,0.95) 0%, rgba(5,5,5,0.72) 45%, rgba(5,5,5,0.55) 100%), linear-gradient(180deg, rgba(5,5,5,0.6) 0%, transparent 30%, transparent 70%, rgba(5,5,5,0.95) 100%)",
          }}
          aria-hidden="true"
        />
        {/* Emerald atmospheric glows */}
        <div
          className="absolute -top-48 -right-48 w-[700px] h-[700px] rounded-full pointer-events-none z-0"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.20) 0%, transparent 60%)" }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-48 -left-24 w-[500px] h-[500px] rounded-full pointer-events-none z-0"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 60%)" }}
          aria-hidden="true"
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy column */}
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-7" data-testid="hero-eyebrow">
              <span
                className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                style={{ boxShadow: "0 0 0 4px rgba(16,185,129,0.18)" }}
              />
              <span className="text-[11px] font-bold tracking-[0.18em] text-emerald-300 uppercase">
                Built for South Carolina
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.0] mb-5" data-testid="hero-headline">
              Verified SC grants.<br />
              <span className="text-emerald-400">Real win odds.</span>
            </h1>
            <p className="text-lg sm:text-xl text-zinc-300 max-w-xl leading-relaxed mb-9" data-testid="hero-lede">
              Zero guesswork. A grant database that shows which ones fit you best — and your realistic odds — before you spend a weekend writing the application.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
              {user ? (
                <Button
                  onClick={() => (window.location.href = "/grants")}
                  className="bg-emerald-500 text-black font-bold text-base px-6 py-5 rounded-sm hover:bg-emerald-400 cta-hover"
                  data-testid="browse-grants-btn"
                >
                  Browse Grants
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      try { localStorage.setItem("pending_trial_start", "1"); } catch { /* ignore */ }
                      setShowEmailAuth(true);
                    }}
                    className="bg-emerald-500 text-black font-bold text-base sm:text-lg px-7 py-7 rounded-sm hover:bg-emerald-400 cta-hover flex items-center gap-2 shadow-[0_15px_50px_-12px_rgba(16,185,129,0.45)] hover:shadow-[0_20px_60px_-12px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all"
                    data-testid="hero-start-trial-btn"
                  >
                    <Sparkle weight="fill" className="w-5 h-5" />
                    Check my win odds — Free, no card
                  </Button>
                  <button
                    onClick={login}
                    className="text-sm text-zinc-400 hover:text-emerald-300 underline underline-offset-4 decoration-zinc-700 hover:decoration-emerald-400 transition-colors flex items-center gap-1.5"
                    data-testid="get-started-btn"
                  >
                    <GoogleLogo weight="bold" className="w-3.5 h-3.5" />
                    or continue with Google
                  </button>
                </>
              )}
            </div>

            {!user && (
              <p className="text-xs text-zinc-400 flex flex-wrap items-center gap-1.5" data-testid="hero-trial-disclaimer">
                <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-emerald-300 font-semibold">No credit card</span>
                <span className="text-zinc-600">·</span>
                <span>Setup in 30 seconds</span>
                <span className="text-zinc-600">·</span>
                <span>Cancel anytime</span>
              </p>
            )}

            {/* Live grant deadline ticker — pulls fresh count from DB on each page load */}
            {!user && <ClosingSoonTicker />}

            {/* Honest, verifiable social proof — no fake numbers */}
            {!user && (
              <p className="text-xs text-zinc-500 italic" data-testid="hero-social-proof">
                Built in South Carolina. Made by an SC founder, for SC nonprofits.
              </p>
            )}
          </div>

          {/* RIGHT — product preview card */}
          <div className="relative" data-testid="hero-product-preview">
            <div
              className="bg-gradient-to-b from-zinc-950 to-zinc-900 border border-zinc-800 rounded-xl p-6 sm:p-7 shadow-[0_30px_80px_-20px_rgba(16,185,129,0.18)]"
              style={{ transform: "perspective(1500px) rotateY(-3deg) rotateX(2deg)" }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">
                  <span>Horry County</span>
                  <span className="w-1 h-1 rounded-full bg-zinc-700" />
                  <span>Tidelands Health</span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-[11px] font-bold tracking-wide text-emerald-300">Strong Shot · 62%</span>
                </div>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-1.5">
                Community Giving &amp; Sponsorships
              </h3>
              <p className="text-sm text-zinc-400 mb-4">
                Tidelands Health Foundation · Rolling deadline
              </p>
              <div className="grid grid-cols-3 gap-3 py-3 border-y border-zinc-800 mb-4">
                <div>
                  <div className="text-[10px] tracking-wider text-zinc-500 uppercase font-semibold mb-1">Award</div>
                  <div className="text-sm font-bold">$500–$10K</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-wider text-zinc-500 uppercase font-semibold mb-1">Avg cycle</div>
                  <div className="text-sm font-bold">6–8 wks</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-wider text-zinc-500 uppercase font-semibold mb-1">Match</div>
                  <div className="text-sm font-bold">87 / 100</div>
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-zinc-300 leading-snug">
                <div className="flex gap-2"><span className="text-emerald-400 font-bold">✓</span> Funder explicitly serves Horry County — your area qualifies</div>
                <div className="flex gap-2"><span className="text-emerald-400 font-bold">✓</span> Grant size matches your annual budget range</div>
                <div className="flex gap-2"><span className="text-emerald-400 font-bold">✓</span> Your mission aligns with their community-health focus</div>
              </div>
              <div className="flex gap-2 mt-4">
                <div className="flex-1 text-center bg-emerald-500 text-black font-bold text-sm py-2.5 rounded">Flash Draft this →</div>
                <div className="px-4 py-2.5 border border-zinc-800 rounded text-sm text-zinc-200 font-semibold">Save</div>
              </div>
            </div>
            {/* Floating $770M stat tile */}
            <div className="absolute -bottom-7 -left-4 sm:-left-10 bg-gradient-to-b from-zinc-950 to-black border border-zinc-800 rounded-xl px-5 py-4 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.6)]">
              <div className="text-3xl font-black text-emerald-400 leading-none tracking-tight">$1.8B</div>
              <div className="text-[10px] tracking-wider text-zinc-400 uppercase font-medium mt-1.5 leading-tight">
                in SC grants<br />awarded in 2024<sup className="text-[8px] opacity-60 ml-0.5">†</sup>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof bar — under hero, above stats */}
      <div className="relative z-10 bg-black border-y border-zinc-900" data-testid="hero-proof-bar">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center gap-x-7 gap-y-2 text-xs sm:text-sm text-zinc-400">
          <div className="flex items-center gap-2.5">
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-black text-black"
              style={{ background: "linear-gradient(135deg, #10B981 0%, #065F46 100%)" }}
              aria-hidden="true"
            >
              YC
            </span>
            <span>
              Trusted by SC nonprofit founders — <b className="text-white font-semibold">Yvonne D. Small Campbell</b>,{" "}
              <i className="text-zinc-500">Daughters of Destiny Network</i>
            </span>
          </div>
          <span className="text-zinc-700 hidden sm:inline">·</span>
          <div><b className="text-white font-semibold">23 of 50</b> Founding Member spots claimed</div>
          <span className="text-zinc-700 hidden sm:inline">·</span>
          <div><b className="text-white font-semibold"><DynamicScoutCount fallback="39" /></b> verified corporate funders</div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pb-3 text-[10px] text-zinc-600 tracking-wide">
          <sup>†</sup> Source: Instrumentl South Carolina 2024 grant funding data ($1,813,067,383 across 24,410 grants). Hero photo: Myrtle Beach, SC — David Frye / Unsplash.
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-20 bg-zinc-950 border-y border-zinc-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="metric-value">{stat.value}</div>
                <div className="text-sm text-zinc-400 uppercase tracking-widest mt-2">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Honesty disclosure */}
          <div className="mt-12 max-w-3xl mx-auto p-5 rounded-md bg-black border border-zinc-800 flex items-start gap-3" data-testid="directory-disclaimer">
            <Info weight="duotone" className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-400 leading-relaxed">
              <span className="text-zinc-200 font-semibold">FlashGrants is a directory and application platform.</span>
              {" "}We do not award grants ourselves and we do not guarantee any award outcomes. Funding decisions are made entirely by the listed funders. We help you find, draft, and track applications &mdash; the wins are yours.
            </p>
          </div>
        </div>
      </section>

      {/* FlashGrants Spotlight — Founding Member feature */}
      <section id="spotlight" className="py-24 bg-black border-b border-zinc-900 relative overflow-hidden" data-testid="spotlight-section">
        {/* Decorative glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-orange-500/10 border border-orange-500/40 text-orange-300 text-xs font-bold tracking-widest uppercase mb-4">
              <Star weight="fill" className="w-3 h-3" />
              FlashGrants Spotlight
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
              Meet Our Founding Members
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              The founders and organizations building South Carolina's nonprofit future — and trusting FlashGrants to help them get there.
            </p>
          </div>

          <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-14 items-center max-w-5xl mx-auto">
            {/* Photo */}
            <div className="relative mx-auto lg:mx-0" data-testid="spotlight-photo-wrap">
              <div className="absolute -inset-2 bg-gradient-to-br from-orange-500/30 via-amber-400/20 to-emerald-500/20 rounded-md blur-lg" />
              <img
                src="/spotlight/yvonne.jpg"
                alt="Yvonne D. Small Campbell, Owner of Daughters of Destiny Network, Inc."
                className="relative w-[260px] sm:w-[300px] lg:w-[340px] rounded-md shadow-2xl border-4 border-white/90"
                data-testid="spotlight-photo"
              />
              <div className="absolute -bottom-3 -right-3 bg-orange-500 text-black font-bold text-xs px-3 py-1.5 rounded-sm shadow-lg" data-testid="spotlight-badge">
                FOUNDING MEMBER · #001
              </div>
            </div>

            {/* Content */}
            <div data-testid="spotlight-content">
              <Quotes weight="fill" className="w-10 h-10 text-orange-400/70 mb-3" />
              <blockquote className="text-lg sm:text-xl text-zinc-200 leading-relaxed font-medium mb-6" data-testid="spotlight-quote">
                I'm honored to be a member of the FlashGrants' family because of its innovative approach to helping organizations and individuals access funding opportunities more efficiently.
                <span className="block mt-3 text-zinc-300">
                  As Executive Producer of Daughters of Destiny, Inc.'s podcast, the partnership strengthens my commitment to continue sharing stories that empower, heal, and inspire. I'm excited about our journey!
                </span>
              </blockquote>

              <div className="border-l-2 border-orange-500/60 pl-4 mb-6" data-testid="spotlight-attribution">
                <p className="text-white font-bold text-lg leading-tight">Yvonne D. Small Campbell</p>
                <p className="text-orange-300 text-sm font-semibold mt-0.5">
                  Owner, Daughters of Destiny Network, Inc.
                </p>
                <p className="text-zinc-400 text-sm flex items-center gap-1.5 mt-1">
                  <Microphone weight="fill" className="w-3.5 h-3.5 text-zinc-500" />
                  Executive Producer &amp; Host, Daughters of Destiny's Podcast
                </p>
              </div>

              <a
                href="mailto:ydsc327@gmail.com"
                className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-orange-500/60 rounded-sm text-sm font-semibold text-white transition-colors"
                data-testid="spotlight-contact-btn"
              >
                <EnvelopeSimple weight="fill" className="w-4 h-4 text-orange-400" />
                Connect with Yvonne
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Founding Member spots — live counter + CTA */}
          <div className="mt-14 max-w-2xl mx-auto text-center" data-testid="spotlight-founding-cta">
            <FoundingSpotsCounter variant="inline" />
            <a
              href="/founding"
              className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-black rounded-sm text-sm font-bold uppercase tracking-widest transition-colors"
              data-testid="spotlight-claim-founding-btn"
            >
              <Star weight="fill" className="w-4 h-4" />
              Claim a Founding Member Spot
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <p className="text-xs text-zinc-500 mt-3">
              Locked-in pricing forever. Cancel anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Free Resource — Start a Nonprofit in SC */}
      <section className="py-16 sm:py-20 border-b border-zinc-900 bg-gradient-to-b from-zinc-950 to-black" data-testid="free-resource-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-[1.5fr_1fr] gap-8 items-center p-8 sm:p-10 rounded-sm bg-zinc-900/40 border border-zinc-800 hover:border-emerald-500/30 transition-colors">
            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-4">
                Free · No signup required
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
                Don't have your 501(c)(3) yet? Start here.
              </h3>
              <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mb-5">
                Our free 8-step roadmap walks SC founders through every filing — Articles of Incorporation, EIN, bylaws, IRS Form 1023-EZ, SC Charities Division — with real costs ($350 total) and real timelines (3–6 months). No fluff, no consultant fees, no "AI-generated" filler.
              </p>
              <a
                href="/start-a-nonprofit-in-sc"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-emerald-500/60 rounded-sm text-sm font-bold uppercase tracking-widest transition-colors"
                data-testid="free-resource-cta"
              >
                Read the free roadmap
                <ArrowRight weight="bold" className="w-4 h-4" />
              </a>
            </div>
            <div className="hidden md:flex flex-col gap-2 text-xs">
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">01</span> <span>Form SC corporation</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">02</span> <span>Get an EIN</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">03</span> <span>Adopt bylaws</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">04</span> <span>Board meeting</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">05</span> <span>File IRS Form 1023-EZ</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">06</span> <span>Register w/ SC Charities</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">07</span> <span>Sales tax exemption</span></div>
              <div className="flex items-baseline gap-2 text-zinc-300"><span className="text-emerald-400 font-mono">08</span> <span>Open bank account</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Open Grants — public preview, no auth */}
      <section id="open-grants" className="py-24 bg-black border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 badge-emerald rounded-sm mb-4">
              <CurrencyDollar weight="fill" className="w-3 h-3" />
              <span>CURRENT OPEN GRANTS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Real Grants Open Right Now
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              A live snapshot of opportunities in our directory &mdash; sign up free to see all <DynamicGrantCount /> and unlock match scoring, drafts, and reminders.
            </p>
          </div>

          {previewGrants.length === 0 ? (
            <div className="text-center text-zinc-500 py-8" data-testid="grants-preview-loading">Loading current opportunities…</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto" data-testid="grants-preview-list">
              {previewGrants.map((g) => (
                <div key={g.grant_id || g.id} className="bg-zinc-950 border border-zinc-800 rounded-md p-5 hover:border-emerald-700 transition flex flex-col" data-testid={`preview-grant-${g.grant_id || g.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-base font-bold text-white leading-snug line-clamp-2">{g.title}</h3>
                  </div>
                  <p className="text-xs text-emerald-400 font-semibold mb-3 truncate">{g.funder_name || g.funder || "Verified SC funder"}</p>
                  <p className="text-sm text-zinc-400 line-clamp-3 mb-4 flex-1">{g.description}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-zinc-300 border-t border-zinc-800 pt-3">
                    <div className="flex items-center gap-1">
                      <CurrencyDollar className="w-3.5 h-3.5 text-emerald-400" />
                      <span>Up to ${(g.amount_max || 0).toLocaleString()}</span>
                    </div>
                    {g.deadline && g.deadline !== "rolling" && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                        <span>{g.deadline}</span>
                      </div>
                    )}
                    {g.category && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                        <span>{g.category}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 text-center">
            <Button
              onClick={login}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm h-12 px-8"
              data-testid="see-all-grants-btn"
            >
              See all <DynamicGrantCount /> open grants — Free <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
            </Button>
            <p className="text-xs text-zinc-500 mt-3">No credit card required. Free tier includes match scoring + 3 applications/month.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 badge-emerald rounded-sm mb-4">
              <Sparkle weight="fill" className="w-3 h-3" />
              <span>FEATURES</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Everything You Need to Win Grants
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              From discovery to post-grant management, FlashGrants streamlines the entire funding lifecycle.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-zinc-950 border border-zinc-800 p-8 rounded-sm card-hover"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section 
        className="py-24 relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1774391368462-0b3bcb562021?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdyZWVuJTIwbGlnaHR8ZW58MHx8fHwxNzc0OTExODU1fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-black/90" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 badge-emerald rounded-sm mb-4">
                <Lightning weight="fill" className="w-3 h-3" />
                <span>FLASH DRAFT</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Flash Draft: Write Grants at Lightning Speed
              </h2>
              <p className="text-zinc-400 mb-6">
                Our system analyzes grant requirements and your nonprofit profile to generate 
                compelling, tailored application drafts. Edit and submit in minutes, not hours.
              </p>
              <ul className="space-y-3">
                {[
                  "Smart, tailored draft generation",
                  "Matched to each grant's requirements",
                  "Edit and customize before submitting",
                  "Unlimited drafts with Premium tier"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-300">
                    <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="flash-draft-container p-6 rounded-sm font-mono text-sm animate-pulse-glow">
              <div className="text-emerald-400 mb-2">// Flash Draft Preview</div>
              <div className="text-zinc-400 mb-4">Generating application for Community Garden Grant...</div>
              <div className="space-y-2 text-zinc-300">
                <p className="text-emerald-300"># Executive Summary</p>
                <p>Green Roots SC seeks $3,500 to establish a community garden serving 200+ families in North Charleston...</p>
                <p className="text-emerald-300 mt-4"># Project Goals</p>
                <p>1. Create 40 raised garden beds<span className="typewriter-cursor" /></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing" className="py-24 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 badge-emerald rounded-sm mb-4">
            <CurrencyDollar weight="fill" className="w-3 h-3" />
            <span>PRICING</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Start Free, Scale as You Grow
          </h2>
          <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
            {pricingAudience === "franchisee"
              ? "Most franchise agreements require documented community giving. Discover local SC nonprofits in your territory, then turn quarterly sponsorships into audit-ready impact PDFs your franchisor can verify."
              : pricingAudience === "business"
              ? "Sponsor a local SC nonprofit — discover events and programs in your county for free, then level up to a named Local Hero partnership."
              : "Begin with our free tier and upgrade when you're ready for unlimited drafts and post-grant management."}
          </p>

          {/* Audience switcher */}
          <div className="mb-12 inline-flex flex-wrap items-center justify-center bg-zinc-900 border border-zinc-800 rounded-md p-1 gap-1" data-testid="landing-audience-switcher">
            <button
              onClick={() => setPricingAudience("nonprofit")}
              className={`px-4 py-2 rounded-sm text-sm font-semibold transition ${
                pricingAudience === "nonprofit"
                  ? "bg-emerald-500 text-black"
                  : "text-zinc-300 hover:text-white"
              }`}
              data-testid="landing-audience-nonprofit"
            >
              Nonprofits
            </button>
            <button
              onClick={() => setPricingAudience("business")}
              className={`px-4 py-2 rounded-sm text-sm font-semibold transition ${
                pricingAudience === "business"
                  ? "bg-emerald-500 text-black"
                  : "text-zinc-300 hover:text-white"
              }`}
              data-testid="landing-audience-business"
            >
              Small Business
            </button>
            <button
              onClick={() => setPricingAudience("franchisee")}
              className={`px-4 py-2 rounded-sm text-sm font-semibold transition ${
                pricingAudience === "franchisee"
                  ? "bg-emerald-500 text-black"
                  : "text-zinc-300 hover:text-white"
              }`}
              data-testid="landing-audience-franchisee"
            >
              Franchisees
            </button>
          </div>

          {/* Franchisee-specific compliance callout — high-intent SEO + conversion signal */}
          {pricingAudience === "franchisee" && (
            <div className="mb-10 max-w-3xl mx-auto bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-5 text-left" data-testid="franchisee-compliance-callout">
              <div className="flex items-start gap-3">
                <Info weight="fill" className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="space-y-1.5">
                  <p className="text-sm text-zinc-200 font-semibold">Most franchise agreements require documented community giving.</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Local Hero turns your sponsorships into <strong className="text-zinc-200">audit-ready quarterly impact PDFs</strong> — verifiable nonprofit acknowledgement letters, dollar amounts, and recipient organization details that corporate HQ can file without follow-up. Used by franchise owners across SC for QSR, hospitality, retail, and home services brands.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className={`grid ${isBizOrFranchisee ? "md:grid-cols-2 max-w-3xl" : "md:grid-cols-3 max-w-5xl"} gap-6 mx-auto`}>
            {/* Free tier — different feature list per audience */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-sm text-left">
              <div className="text-sm text-zinc-400 uppercase tracking-widest mb-2">
                {isBizOrFranchisee ? "Local Sponsor" : "Free"}
              </div>
              <div className="text-4xl font-mono font-bold text-white mb-4">$0</div>
              <ul className="space-y-3 mb-8">
                {isBizOrFranchisee ? (
                  <>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Browse Local Sponsorship Opportunities (no limit)
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Filter by county, industry, budget
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Express interest — nonprofit emails you directly
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Supplier-diversity directory (Boeing, BMW, Duke, Michelin)
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Cross-collab visibility with SC nonprofits
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                      1 Flash Draft per month
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                      3 grant applications per month
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                      Browse all grants
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                      Bookmark grants
                    </li>
                    <li className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                      Application tracker
                    </li>
                  </>
                )}
              </ul>
              <Button
                onClick={login}
                variant="outline"
                className="w-full border-zinc-700 text-white hover:border-zinc-500 rounded-sm"
                data-testid="pricing-free-btn"
              >
                {isBizOrFranchisee ? "Start Sponsoring Free" : "Get Started"}
              </Button>
            </div>

            {/* Second card — Local Hero for business/franchisee, Starter for nonprofit */}
            {isBizOrFranchisee ? (
              <div className="bg-black border-2 border-emerald-500/50 p-8 rounded-sm text-left pricing-pro relative" data-testid="pricing-local-hero-card">
                <div className="absolute -top-3 right-4 badge-emerald px-2 py-0.5 text-xs">RECOMMENDED</div>
                <div className="text-sm text-emerald-400 uppercase tracking-widest mb-2">Local Hero</div>
                <div className="text-4xl font-mono font-bold text-white mb-4">$5K<span className="text-lg text-zinc-500">/yr</span></div>
                {pricingAudience === "franchisee" ? (
                  <>
                    <p className="text-xs text-emerald-300 mb-4 font-mono tracking-wide">Built for franchise community-giving compliance</p>
                    <ul className="space-y-3 mb-8">
                      <li className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Quarterly Impact PDF — audit-ready for franchisor HQ
                      </li>
                      <li className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Verifiable receipts + nonprofit acknowledgement letters
                      </li>
                      <li className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Custom Local Hero microsite — show corporate you're rooted locally
                      </li>
                      <li className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Named recognition on the nonprofit's site & events
                      </li>
                      <li className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Concierge intros to 3 SC nonprofits matching your franchise's brand pillars
                      </li>
                      <li className="flex items-start gap-2 text-sm text-zinc-300">
                        <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Leaderboard placement among SC Local Heroes
                      </li>
                    </ul>
                  </>
                ) : (
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Named recognition on the nonprofit's site, events & materials
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Custom Local Hero microsite for your business
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Quarterly Impact PDF (tax-friendly summary)
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Leaderboard placement among SC Local Heroes
                    </li>
                    <li className="flex items-start gap-2 text-sm text-zinc-300">
                      <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      Concierge introductions to 3 matching nonprofits
                    </li>
                  </ul>
                )}
                <Button
                  onClick={() => window.location.href = '/local-hero'}
                  className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm cta-hover"
                  data-testid="pricing-local-hero-btn"
                >
                  Learn About Local Hero
                </Button>
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-sm text-left" data-testid="pricing-starter-card">
                <div className="text-sm text-zinc-400 uppercase tracking-widest mb-2">Starter</div>
                <div className="text-4xl font-mono font-bold text-white mb-4">$19.99<span className="text-lg text-zinc-500">/mo</span></div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                    5 Flash Drafts per month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                    Unlimited applications
                  </li>
                  <li className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                    Calendar + deadline reminders
                  </li>
                  <li className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                    Auto-saving across devices
                  </li>
                  <li className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                    Email support
                  </li>
                </ul>
                <Button
                  onClick={() => window.location.href = '/pricing'}
                  variant="outline"
                  className="w-full border-zinc-700 text-white hover:border-emerald-500 rounded-sm"
                  data-testid="pricing-starter-btn"
                >
                  Choose Starter
                </Button>
              </div>
            )}
            
            {/* Premium — only visible for nonprofit audience */}
            {!isBizOrFranchisee && (
            <div className="bg-black border-2 border-emerald-500/50 p-8 rounded-sm text-left pricing-pro relative">
              <div className="absolute -top-3 right-4 badge-emerald px-2 py-0.5 text-xs">RECOMMENDED</div>
              <div className="text-sm text-emerald-400 uppercase tracking-widest mb-2">Premium</div>
              <div className="text-4xl font-mono font-bold text-white mb-4">$49.99<span className="text-lg text-zinc-500">/mo</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  Unlimited Flash Drafts
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  Post-Grant Manager
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  Budget & milestone tracking
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  Flash Reports
                </li>
                <li className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
                  Funder Insights
                </li>
              </ul>
              <Button 
                onClick={() => window.location.href = '/pricing'}
                className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm cta-hover"
                data-testid="pricing-pro-btn"
              >
                Upgrade to Premium
              </Button>
            </div>
            )}
          </div>

          {/* Promo Code Section */}
          <div className="max-w-3xl mx-auto mt-8" data-testid="landing-promo-section">
            <div className="bg-emerald-500/5 border-2 border-emerald-500/30 rounded-sm p-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <Ticket weight="duotone" className="w-7 h-7 text-emerald-400" />
                  <div className="text-left">
                    <p className="font-bold text-white text-lg">Have a promo code?</p>
                    <p className="text-sm text-zinc-400">Redeem it for free Premium access</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-1 w-full sm:w-auto">
                  <Input
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && (user ? redeemPromo() : login())}
                    className="bg-zinc-900 border-zinc-700 text-white uppercase tracking-wider font-mono text-lg h-12"
                    data-testid="landing-promo-input"
                  />
                  <Button
                    onClick={user ? redeemPromo : login}
                    disabled={promoLoading}
                    className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm flex-shrink-0 h-12 px-6 text-base"
                    data-testid="landing-promo-btn"
                  >
                    {promoLoading ? "Redeeming..." : user ? "Redeem" : "Sign In to Redeem"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="about" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 badge-emerald rounded-sm mb-4">
                <Users weight="fill" className="w-3 h-3" />
                <span>COMMUNITY IMPACT</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Empowering SC Nonprofits
              </h2>
              <p className="text-zinc-400 mb-6">
                FlashGrants was built specifically for South Carolina's vibrant nonprofit community. 
                We understand the unique challenges local organizations face in securing funding 
                and streamline the process so you can focus on what matters most—your mission.
              </p>
              <Button 
                onClick={login}
                className="bg-emerald-500 text-black font-bold rounded-sm hover:bg-emerald-400 cta-hover"
                data-testid="join-community-btn"
              >
                Join the Community
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="/images/mickey-james-jazz-festival.jpg"
                alt="Mickey James celebrating at the Myrtle Beach Jazz Festival"
                className="w-full h-[420px] object-cover rounded-sm border border-zinc-800"
                style={{ objectPosition: "center 30%" }}
                data-testid="community-impact-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-sm pointer-events-none" />
              <p className="mt-3 text-xs text-zinc-500 italic leading-relaxed" data-testid="community-impact-caption">
                Mickey James — Founder &amp; CEO of Crossroads Events Productions and the Myrtle Beach Jazz Festival. The work we build for is real, and it's already happening on SC ground.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-zinc-950 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
              <span className="font-bold">FlashGrants</span>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-zinc-400">
              <a href="/start-a-nonprofit-in-sc" className="hover:text-emerald-400 transition-colors" data-testid="footer-start-nonprofit">Start a Nonprofit in SC</a>
              <a href="/coalitions/playbook" className="hover:text-emerald-400 transition-colors" data-testid="footer-coalition-playbook">Coalition Playbook</a>
              <a href="/founding" className="hover:text-emerald-400 transition-colors" data-testid="footer-founding">Founding Members</a>
              <a href="#pricing" className="hover:text-emerald-400 transition-colors">Pricing</a>
            </nav>
            <p className="text-sm text-zinc-500">
              © 2026 FlashGrants
            </p>
          </div>
        </div>
      </footer>

      {/* Email Auth Modal */}
      {showEmailAuth && <EmailAuthModal onClose={() => setShowEmailAuth(false)} />}
    </div>
  );
};

export default LandingPage;
